import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type PendingScenarioNavigation = {
    proceed: () => void;
};

export function useScenarioNavigationBlocker(
    when: boolean,
    onBlocked: (pending: PendingScenarioNavigation) => void,
) {
    const location = useLocation();
    const navigate = useNavigate();
    const whenRef = useRef(when);
    whenRef.current = when;

    const locationRef = useRef(location);
    locationRef.current = location;

    const onBlockedRef = useRef(onBlocked);
    onBlockedRef.current = onBlocked;

    useEffect(() => {
        if (!when) {
            return;
        }

        const handleClick = (event: MouseEvent) => {
            if (!whenRef.current || event.defaultPrevented || event.button !== 0) {
                return;
            }
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const anchor = target.closest('a[href]');
            if (!(anchor instanceof HTMLAnchorElement)) {
                return;
            }
            if (anchor.target && anchor.target !== '_self') {
                return;
            }

            const href = anchor.getAttribute('href');
            if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
                return;
            }

            let url: URL;
            try {
                url = new URL(href, window.location.href);
            } catch {
                return;
            }

            if (url.origin !== window.location.origin) {
                return;
            }

            const current = locationRef.current;
            if (
                url.pathname === current.pathname &&
                url.search === current.search &&
                url.hash === current.hash
            ) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const destination = `${url.pathname}${url.search}${url.hash}`;
            onBlockedRef.current({
                proceed: () => navigate(destination),
            });
        };

        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [navigate, when]);
}
